import { useTranslations } from 'next-intl';
import React, { ReactNode } from 'react';

import { CastDto } from '@cineamo/api/models/movie/CastDto.types';
import { GenreDto } from '@cineamo/api/models/movie/GenreDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';

import MovieDetailsTable, { MovieDetailRow } from './MovieDetailsTable';

/**
 * Compound component that combines movie overview text and details table.
 * Provides two-column layout with overview on left and details table on right.
 * Supports custom overview slot for expandable text functionality.
 * Uses genre translations from ui package locales.
 */
const MovieInfoSection: React.FC<{
    className?: string;
    overview?: string;
    overviewSlot?: ReactNode;
    cast?: Array<CastDto>;
    genres?: Array<GenreDto>;
    details?: MovieDetailRow[];
    hintSlot?: ReactNode;
}> = ({ className, overview, overviewSlot, cast, genres, details, hintSlot }) => {
    const t = useTranslations();

    return (
        <div className={cn(className, 'pt-24 md:pt-96 mx-auto my-28 h-full lg:px-16')}>
            <div className={cn('h-full lg:grid lg:grid-cols-5 lg:space-x-24 space-y-24 lg:space-y-0 text-body-16')}>
                <div className="flex flex-col col-span-3">
                    {overviewSlot
                        ? overviewSlot
                        : overview && <p className="text-body-16 whitespace-pre-wrap">{overview}</p>}
                    {cast && cast.length > 0 && (
                        <div className="text-body-14 mt-16 tracking-regular opacity-80">
                            {t('movie.cast')}:{' '}
                            {cast
                                .slice(0, 5)
                                .map((castMember) => castMember.name)
                                .join(' · ')}
                        </div>
                    )}
                    {genres && genres.length > 0 && (
                        <div className="text-body-10 tracking-wide mt-16">
                            <span className="flex space-x-4">
                                {genres.map((genre) => (
                                    <span
                                        key={genre.id}
                                        className="border-2 border-[rgba(var(--surface-color-rgb),0.2)] rounded-4 px-6 py-2">
                                        {typeof genre.id === 'string' ? genre.name : t(`genres.tmdb-genre-${genre.id}`)}
                                    </span>
                                ))}
                            </span>
                        </div>
                    )}
                    {hintSlot}
                </div>
                {details && details.length > 0 && (
                    <MovieDetailsTable
                        details={details}
                        variant="default"
                    />
                )}
            </div>
        </div>
    );
};

export default MovieInfoSection;
